// views/AdminCompaniesView.jsx
// ─────────────────────────────────────────────────────────────
// Company & payroll oversight (read-only). Renders inside the admin
// shell <main> — no own header/sidebar. Dark theme to match console.
// Endpoints: /admin/companies, /admin/companies/:companyId, /admin/payroll-runs
// (adjust prefix to match your admin overview router mount).
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices"; // adjust depth to your tree

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;
const RUN_CLS = {
  completed:  "bg-[#00C896]/15 text-[#00C896]",
  partial:    "bg-amber-400/15 text-amber-400",
  failed:     "bg-red-500/15 text-red-400",
  processing: "bg-blue-400/15 text-blue-400",
  pending:    "bg-white/10 text-white/50",
};

export default function AdminCompaniesView() {
  const [tab, setTab]         = useState("companies"); // companies | runs
  const [companies, setCompanies] = useState([]);
  const [runs, setRuns]       = useState([]);
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = tab === "companies" ? "/mtrade/admin/companies" : "/mtrade/admin/payroll-runs";
    apiClient.get(url)
      .then(r => { if (r.data.status) tab === "companies" ? setCompanies(r.data.data) : setRuns(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab]);

  const openCompany = async (companyId) => {
    try {
      const r = await apiClient.get(`/mtrade/admin/companies/${companyId}`);
      if (r.data.status) setDetail(r.data.data);
    } catch {}
  };

  return (
    <div className="text-white">
      {/* Title + tabs */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Companies &amp; Payroll</h1>
          <p className="text-white/40 text-sm mt-0.5">Oversight of company accounts and payroll runs</p>
        </div>
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {[["companies", "Companies"], ["runs", "Payroll Runs"]].map(([v, l]) => (
            <button key={v} onClick={() => { setTab(v); setDetail(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${tab === v ? "bg-[#00C896]/15 text-[#00C896]" : "text-white/40 hover:text-white/70"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />)}</div>
      ) : tab === "companies" ? (
        detail ? (
          <div className="space-y-4">
            <button onClick={() => setDetail(null)} className="text-[#00C896] text-sm font-semibold flex items-center gap-1">
              <i className="ri-arrow-left-line" />Back to list
            </button>

            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
              <p className="text-white font-bold">{detail.company.name}</p>
              <p className="text-white/40 text-xs">RC: {detail.company.rcNumber || "—"} · {detail.company.industry || "—"}</p>
              <p className="text-white font-bold text-2xl mt-3">{naira(detail.company.companyNairaBalance)}</p>
              <p className="text-white/40 text-xs">wallet balance</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
                <p className="text-white/40 text-xs">Members</p>
                <p className="text-white font-bold text-lg">{detail.members.length}</p>
              </div>
              <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
                <p className="text-white/40 text-xs">Staff</p>
                <p className="text-white font-bold text-lg">{detail.staff.length}</p>
              </div>
            </div>

            <div>
              <p className="text-white/30 text-[11px] font-semibold uppercase tracking-widest mb-2">Recent payroll runs</p>
              <div className="space-y-2">
                {detail.recentRuns.length === 0 ? <p className="text-white/30 text-sm">No runs yet</p>
                : detail.recentRuns.map(r => (
                  <div key={r._id} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-semibold">{naira(r.totalAmount)}</p>
                      <p className="text-white/40 text-xs">{r.paidCount}/{r.lines?.length} paid · {new Date(r.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RUN_CLS[r.status] || RUN_CLS.pending}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {companies.length === 0 ? <p className="text-white/30 text-sm text-center py-12">No companies yet</p>
            : companies.map(c => (
              <button key={c.companyId} onClick={() => openCompany(c.companyId)}
                className="w-full bg-[#0f0f0f] border border-white/5 hover:border-[#00C896]/30 rounded-2xl p-4 text-left flex items-center justify-between transition-colors">
                <div>
                  <p className="text-white font-semibold text-sm">{c.name}</p>
                  <p className="text-white/40 text-xs">{c.activeStaff} staff · {naira(c.companyNairaBalance)} balance</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-[#00C896]/15 text-[#00C896]" : "bg-white/10 text-white/50"}`}>{c.status}</span>
              </button>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-2">
          {runs.length === 0 ? <p className="text-white/30 text-sm text-center py-12">No payroll runs yet</p>
          : runs.map(r => (
            <div key={r._id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-sm">{naira(r.totalAmount)}</p>
                <p className="text-white/40 text-xs">{r.paidCount}/{r.lines?.length} paid · {r.failedCount} failed · {new Date(r.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RUN_CLS[r.status] || RUN_CLS.pending}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
